import React, { createContext, useEffect, useRef, useState } from 'react'
import { CostsHeader } from "./components/costsHeader/CostsHeader";
import { CostsTable } from "./components/costsTable/CostsTable";
import { RowsPerPage } from "../../constants/RowsPerPage";
import { CustomSelect } from "../../components/customSelect/CustomSelect";
import { useDispatch, useSelector } from "react-redux";
import { IComment, IExpense, IStatistic, ISummaryEmployee, ITask, ITimesheet, IUser } from "../../models";
import { getBearer } from "../../functions/getBearer";
import axios from "axios";
import { getApiLink } from "../../functions/getApiLink";
import { setChosenTimesheet, setExpenses, setTasks } from "../../storage/toolkit";
import { CostsStyles } from "./Costs.styles";
import { DownSidebar } from "../../components/downSidebar/DownSidebar";
import { useParams } from "react-router-dom";
import { SetStatistic } from "../../api/SetStatistic";
import { Translate } from "../../components/translate/Translate";
import { GetAccessToken } from '../../api/GetAccessToken';
import { SetTimesheet } from "../../api/SetTimesheet";
import { SetExpenses } from "../../api/SetExpenses";

interface ICostsProps {

}

export const BlockToEdit: any = createContext(null)
export const BlockToDuplicate: any = createContext(null)
export const FixedTopEdit: any = createContext(null)
export const AmountStatistic: any = createContext(null)

export const Costs: React.FC<ICostsProps> = () => {

    const dispatch = useDispatch()

    const { timesheetId }: any = useParams()

    const user: IUser = useSelector((state: any) => state.toolkit.user)
    const expenseList: IExpense[] = useSelector((state: any) => state.toolkit.expenses)
    const chosenTimesheet: ITimesheet = useSelector((state: any) => state.toolkit.chosenTimesheet)
    const timesheetStatistic: IStatistic = useSelector((state: any) => state.toolkit.timesheetStatistic)
    const summaryEmployees: ISummaryEmployee = useSelector((state: any) => state.toolkit.summaryEmployees)
    const timesheet: ITimesheet[] = useSelector((state: any) => state.toolkit.timesheet)

    const [rowsSelectValue, setRowsSelectValue] = useState(RowsPerPage()[0])
    const [itemToEdit, setItemToEdit] = useState<IExpense>()
    const [itemToDuplicate, setItemToDuplicate] = useState<IExpense>()
    const [isOpenDownSidebar, setIsOpenDownSidebar] = useState(false)
    const [isLoad, setIsLoad] = useState(false)
    const [isWindowMobile, setIsWindowMobile] = useState(false)

    // Loading state to prevent flicker
    const mountedRef = useRef(true);
    const lastTimesheetIdRef = useRef<string | undefined>(undefined);
    const chosenTimesheetSetRef = useRef<string | undefined>(undefined);

    // MAIN EFFECT: Handle page load and navigation
    useEffect(() => {
        mountedRef.current = true;
        
        const loadData = async () => {
            const currentYear = new Date().getFullYear();
            
            if (timesheetId) {
                // VIEWING SPECIFIC COSTS
                window.scrollTo(0, 0);
                
                try {
                    const [timesheetRes, expensesRes] = await Promise.all([
                        axios.get(getApiLink(`/api/timesheet/${timesheetId}/`)),
                        axios.get(getApiLink(`/api/timesheet/expenses/?timesheet_id=${timesheetId}`))
                    ]);
                    
                    if (!mountedRef.current) return;
                    
                    chosenTimesheetSetRef.current = timesheetRes.data?.id;
                    dispatch(setChosenTimesheet(timesheetRes.data));
                    dispatch(setExpenses(expensesRes.data));
                    SetStatistic(dispatch, timesheetId);
                } catch (err) {
                    console.error("Error loading specific costs:", err);
                }
            } else {
                // MAIN PAGE - load current user's data
                dispatch(setExpenses([]));
                dispatch(setTasks([]));
                chosenTimesheetSetRef.current = undefined;
                
                await SetTimesheet(dispatch, currentYear);
            }
        };
        
        // Always reload when timesheetId changes
        if (lastTimesheetIdRef.current !== timesheetId) {
            lastTimesheetIdRef.current = timesheetId;
            chosenTimesheetSetRef.current = undefined;
            loadData();
        }
        
        return () => {
            mountedRef.current = false;
        };
    }, [timesheetId, dispatch]);

    // When timesheet list loads on main page, select appropriate month
    useEffect(() => {
        if (timesheetId) return;
        if (!timesheet?.length) {
            if (chosenTimesheetSetRef.current) {
                chosenTimesheetSetRef.current = undefined;
                dispatch(setChosenTimesheet({}));
                dispatch(setExpenses([]));
            }
            return;
        }
        if (!user?.id) return;
        
        // CRITICAL: If current chosenTimesheet belongs to another user, ignore it
        if (chosenTimesheet?.user?.id && chosenTimesheet.user.id !== user.id) {
            chosenTimesheetSetRef.current = undefined;
        }
        
        const cookieMonth = document.cookie.match(/timesheetMonth=(\d+)/);
        const selectedMonth = cookieMonth ? Number(cookieMonth[1]) : (new Date().getMonth() + 1);
        
        const matching = timesheet.find((item) => {
            const monthFromDate = Number(item.date.split("/")[1]);
            return monthFromDate === selectedMonth;
        });
        
        const selectedTimesheet = matching || timesheet[0];
        
        if (selectedTimesheet?.id && chosenTimesheetSetRef.current !== selectedTimesheet.id) {
            chosenTimesheetSetRef.current = selectedTimesheet.id;
            dispatch(setChosenTimesheet(selectedTimesheet));
        }
    }, [timesheet, timesheetId, user?.id, dispatch, chosenTimesheet?.user?.id]);

    // Load expenses when chosenTimesheet changes on main page
    useEffect(() => {
        if (timesheetId) return;
        if (!chosenTimesheet?.id) return;
        if (!user?.id) return;
        
        // CRITICAL: Verify it's current user's timesheet
        if (chosenTimesheet.user?.id && chosenTimesheet.user.id !== user.id) {
            console.log("[Costs] Detected foreign timesheet, clearing...");
            dispatch(setChosenTimesheet({}));
            dispatch(setExpenses([]));
            return;
        }
        
        const controller = new AbortController();
        
        const loadExpenses = async () => {
            dispatch(setExpenses([])); // Clear before loading
            
            try {
                getBearer("get");
                const expensesRes = await axios.get(
                    getApiLink(`/api/timesheet/expenses/?timesheet_id=${chosenTimesheet.id}`),
                    { signal: controller.signal }
                );
                
                if (controller.signal.aborted) return;
                
                dispatch(setExpenses(expensesRes.data));
                SetStatistic(dispatch, chosenTimesheet.id);
            } catch (err: any) {
                if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
                console.error("Error loading expenses:", err);
            }
        };
        
        loadExpenses();
        
        return () => {
            controller.abort();
        };
    }, [chosenTimesheet?.id, timesheetId, user?.id, dispatch]);

    useEffect(() => {
        if (summaryEmployees?.all?.length && timesheetId) {
            const found = summaryEmployees.all.find(item => item.id === timesheetId) 
                       || summaryEmployees.favourite?.find(item => item.id === timesheetId);
            
            if (found && chosenTimesheetSetRef.current !== found.id) {
                chosenTimesheetSetRef.current = found.id;
                dispatch(setChosenTimesheet(found));
            }
        }
    }, [summaryEmployees, timesheetId, dispatch]);

    useEffect(() => {
        setIsWindowMobile(window.innerWidth < 576)
    }, [])

    useEffect(() => {
        if (isLoad) return;

        setRowsSelectValue(expenseList.length > +RowsPerPage()[0].value ? RowsPerPage()[0] : RowsPerPage()[3])
        setTimeout(() => {
            setIsLoad(true)
        }, 1000)
    }, [expenseList, isLoad])

    const handleAddRows = () => {
        const plusCount = window.innerWidth < 768 ? 10 : 20

        setRowsSelectValue({
            value: expenseList.length <= +rowsSelectValue.label + plusCount ? 0 : rowsSelectValue.value + plusCount,
            label: expenseList.length <= +rowsSelectValue.label + plusCount ? "All" : String(+rowsSelectValue.label + plusCount)
        })
    }

    const [comments, setComments] = useState<IComment[]>([])

    useEffect(() => {
        if (!chosenTimesheet?.id) return;

        getBearer("get")
        axios.get(getApiLink(`/api/comment/?timesheet_id=${chosenTimesheet.id}`)).then(({ data }) => {
            setComments(data)
        })

    }, [chosenTimesheet])

    const [isFixedEditBlock, setIsFixedEditBlock] = useState(false)
    const [amountStatistic, setAmountStatistic] = useState(0)

    return (
        <BlockToDuplicate.Provider value={setItemToDuplicate}>
            <AmountStatistic.Provider value={setAmountStatistic}>
                <FixedTopEdit.Provider value={setIsFixedEditBlock}>
                    <BlockToEdit.Provider value={setItemToEdit}>
                        <CostsStyles style={{ paddingBottom: isOpenDownSidebar ? "270px" : (isWindowMobile ? "60px" : "80px") }}
                            className={`section-table ${isFixedEditBlock && "fixed-edit-block"}`}>

                            <CostsHeader itemToDuplicate={itemToDuplicate} setItemToDuplicate={setItemToDuplicate} isFixedEditBlock={isFixedEditBlock} itemToEdit={itemToEdit} />
                            {chosenTimesheet?.id && <CostsTable itemToEdit={itemToEdit} rowsSelectValue={rowsSelectValue} />}

                            <div className="section-table__footer">
                                <div className="section-table__row-per-page visible-on-mob">
                                    <span>
                                        <Translate>costs_page.table.rows_per_page</Translate>
                                    </span>

                                    <CustomSelect list={RowsPerPage()} defaultValue={RowsPerPage()[3]}
                                        selectValue={rowsSelectValue}
                                        setSelectedItem={setRowsSelectValue} />
                                </div>
                                {rowsSelectValue.value !== 0 && expenseList.length > rowsSelectValue.value &&
                                    <button onClick={handleAddRows} className="section-table__see-more btn"
                                        type="button">
                                        <Translate>costs_page.table.show_more</Translate>
                                        <svg width="15" height="15" viewBox="0 0 15 15">
                                            <use xlinkHref="#arrow-down"></use>
                                        </svg>
                                    </button>}
                                <div className="section-table__row-per-page visible-on-desktop">
                                    <span>
                                        <Translate>costs_page.table.rows_per_page</Translate>
                                    </span>

                                    <CustomSelect list={RowsPerPage()} defaultValue={RowsPerPage()[3]}
                                        selectValue={rowsSelectValue}
                                        setSelectedItem={setRowsSelectValue} />
                                </div>
                            </div>
                        </CostsStyles>

                        <DownSidebar comments={comments}
                            amountStatistic={amountStatistic}
                            setComments={setComments}
                            type={"cost"} statisticAllAmount={timesheetStatistic.all_sum}
                            statisticAllElements={timesheetStatistic.expenses}
                            setIsOpenDownSidebar={setIsOpenDownSidebar} />
                    </BlockToEdit.Provider>
                </FixedTopEdit.Provider>
            </AmountStatistic.Provider>
        </BlockToDuplicate.Provider>
    )
}
